// src/products/dataroom/components/SetupWizard/SummaryStep.js
import React from 'react';
import { CheckCircle, Building2, Users, UserPlus, ArrowRight } from 'lucide-react';

const SummaryStep = ({ projectName, setupData, onComplete, onBack }) => {
  const { processType, parties, teams, users } = setupData;

  const processTypeNames = {
    'ma': 'M&A',
    'investment': 'Investment',
    'partnership': 'Partnership',
    'other': 'Other'
  };

  const getTotalTeams = () => {
    if (!teams) return 0;
    return Object.values(teams).reduce((total, partyTeams) => total + partyTeams.length, 0);
  };

  const getTotalUsers = () => {
    if (!users) return 0;
    return Object.values(users).reduce((total, partyUsers) => total + partyUsers.length, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Setup Complete!
        </h2>
        <p className="text-gray-600">
          Your dataroom is ready with parties, teams, and permissions configured.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Dataroom Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Building2 className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="font-semibold text-blue-900">Dataroom</h3>
          </div>
          <p className="text-blue-800 font-medium">{projectName}</p>
          <p className="text-sm text-blue-600">
            {processTypeNames[processType] || 'Custom'} Process
          </p>
        </div>

        {/* Parties */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Building2 className="w-5 h-5 text-purple-600 mr-2" />
            <h3 className="font-semibold text-purple-900">Parties</h3>
          </div>
          <p className="text-purple-800 font-medium text-2xl">{parties?.length || 0}</p>
          <p className="text-sm text-purple-600">Bidding organizations</p>
        </div>

        {/* Teams */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Users className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="font-semibold text-green-900">Teams</h3>
          </div>
          <p className="text-green-800 font-medium text-2xl">{getTotalTeams()}</p>
          <p className="text-sm text-green-600">Permission groups</p>
        </div>

        {/* Users */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <UserPlus className="w-5 h-5 text-orange-600 mr-2" />
            <h3 className="font-semibold text-orange-900">Users</h3>
          </div>
          <p className="text-orange-800 font-medium text-2xl">{getTotalUsers()}</p>
          <p className="text-sm text-orange-600">Invited users</p>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Setup Details
        </h3>
        
        <div className="space-y-4">
          {parties?.map((party) => {
            const partyTeams = teams?.[party.id] || [];
            const partyUsers = users?.[party.id] || [];
            
            return (
              <div key={party.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                {/* Party Header */}
                <div className="flex items-center mb-3">
                  <div 
                    className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                    style={{ backgroundColor: party.color }}
                  />
                  <span 
                    className="px-2 py-1 text-xs font-bold rounded mr-3"
                    style={{ 
                      backgroundColor: party.color,
                      color: 'white'
                    }}
                  >
                    {party.code}
                  </span>
                  <h4 className="font-semibold text-gray-900">{party.name}</h4>
                </div>

                {/* Teams and Users */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-7">
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Teams ({partyTeams.length})</h5>
                    {partyTeams.length > 0 ? (
                      <ul className="space-y-1">
                        {partyTeams.map((team) => (
                          <li key={team.id} className="text-sm text-gray-600 flex items-center">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                            {team.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No teams configured</p>
                    )}
                  </div>

                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Users ({partyUsers.length})</h5>
                    {partyUsers.length > 0 ? (
                      <ul className="space-y-1">
                        {partyUsers.map((user) => (
                          <li key={user.id} className="text-sm text-gray-600 flex items-center">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                            {user.email}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No users invited yet</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Next Steps</h4>
        <ul className="space-y-1 text-sm text-blue-800">
          <li>• Go to Parties, Teams in the nav menu to see the created groups</li>
          <li>• Set permissions for each team before inviting users.</li>
          <li>• Users are not yet invited (emailed) - do this from Users in the menu!</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="flex items-center px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Users
        </button>
        
        <button
          onClick={() => onComplete(setupData)}
          className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Complete Setup
          <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default SummaryStep;

